import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { CatalogCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Catalog Categories',
    description: 'Manage catalog categories',
    permissionModuleKey: 'catalog_category',
    route: {
      list: '/catalog-category',
      new: '/catalog-category/new',
      detail: (id: number) => `/catalog-category/${id}`,
      edit: (id: number) => `/catalog-category/${id}/edit`,
    },
    api: {
      list: '/catalog-categories',
      detail: (id: number) => `/catalog-categories/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: CatalogCategory) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: CatalogCategory[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/catalog-category/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: CatalogCategory) => {
      window.location.href = `/catalog-category/${item.id}`;
    },
    keyExtractor: (item: CatalogCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new category.',
    noSearchResults: 'No categories found matching your search criteria.',
    noFilterResults: 'No categories match the selected filters.',
  },
  delete: {
    title: 'Delete Category',
    message: 'Are you sure you want to delete this category? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete category. Please try again.',
    handler: async (id: number) => {
      const { deleteCatalogCategory } = await import('../../../services/mutations');
      await deleteCatalogCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Category Details',
    notFoundTitle: 'Category not found',
    notFoundMessage: 'The category you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Categories',
    defaultTitle: 'Unnamed Category',
    getTitle: (item: CatalogCategory) => {
      return item.name || `Category #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Category',
      subtitle: 'Add a new catalog category',
      submitText: 'Create Category',
    },
    edit: {
      title: 'Edit Category',
      subtitle: 'Update category information',
      submitText: 'Update Category',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading category...',
    errorMessage: 'Failed to save category. Please try again.',
    create: async (data: Partial<CatalogCategory>) => {
      const { createCatalogCategory } = await import('../../../services/mutations');
      return await createCatalogCategory(data);
    },
    update: async (id: number, data: Partial<CatalogCategory>) => {
      const { updateCatalogCategory } = await import('../../../services/mutations');
      return await updateCatalogCategory(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter category name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Category Information',
        icon: '📂',
        fields: ['name'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<CatalogCategory>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: CatalogCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📂',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: CatalogCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<CatalogCategory> => {
  return {
    name: '',
  };
};

export const mapItemToFormData = (item: CatalogCategory): Partial<CatalogCategory> => {
  return {
    name: item.name || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: CatalogCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

